import React, {useRef, useMemo, useEffect, useState} from 'react';
import {useFrame} from '@react-three/fiber';
import * as THREE from 'three';
import {getCachedMarkerTexture, STATUS_COLORS, type RTLSMarkerData} from './markerUtils';
import {MarkerHoverLabel} from './MarkerHoverLabel';

const HOVER_DELAY_MS = 200;

// Equipment body color — amber for all equipment types
const EQUIPMENT_MODEL_COLOR = '#F59E0B';

// Personnel body color — blue
const PERSONNEL_MODEL_COLOR = '#1D4ED8';

interface AssetMarkerProps {
  data: RTLSMarkerData;
  highlighted?: boolean;
  showLabel?: boolean;
  onClick?: (id: string) => void;
  onDoubleClick?: (pos: {x: number; y: number; z: number}) => void;
  model3D?: THREE.Object3D | null;
  modelScale?: number;
  dimmed?: boolean;
  isPlayback?: boolean;
}

// Human-readable status. Falls back to titlecase if unknown.
function formatStatus(s: string): string {
  switch (s) {
    case 'active': return 'Active';
    case 'idle': return 'Idle';
    case 'alert': return 'Alert';
    case 'offline': return 'Offline';
    case 'down': return 'Down';
    case 'maintenance': return 'Maintenance';
    default: return s.charAt(0).toUpperCase() + s.slice(1);
  }
}

// TODO M3: trail playback — removed onPlay, isPlaying, hasTrail, overridePosition,
// movementDirection props and PlayButton component. Re-add when playback is implemented.

/**
 * Shortest-path angle lerp. Avoids 359° → 1° spinning through the long way.
 */
function lerpAngle(current: number, target: number, t: number): number {
  let diff = target - current;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return current + diff * t;
}

// Yaw offset applied to atan2(dy, dx) so the model's baked forward aligns
// with the direction of travel. Calibrated visually against a heading arrow
// during playback — do not change without re-running that calibration.
const EQUIPMENT_YAW_OFFSET = Math.PI;
const PERSONNEL_YAW_OFFSET = Math.PI / 4;

// Minimum XY distance (scene units) between successive polled positions for
// a new live-mode heading to be accepted. Below this, the delta is treated
// as polling noise and yaw holds. Raise if jitter causes yaw flicker.
const LIVE_YAW_MOVE_THRESHOLD = 2.0;

export const AssetMarker: React.FC<AssetMarkerProps> = React.memo(
  ({data, highlighted = false, showLabel = true, onClick, onDoubleClick, model3D, modelScale = 1, dimmed = false, isPlayback = false}) => {
    const groupRef = useRef<THREE.Group>(null);
    const modelGroupRef = useRef<THREE.Group>(null);
    const currentYawRef = useRef(0);
    const [hovered, setHovered] = useState(false);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const targetPosition = useRef(
      new THREE.Vector3(data.position.x, data.position.y, data.position.z),
    );
    const currentPosition = useRef(
      new THREE.Vector3(data.position.x, data.position.y, data.position.z),
    );
    const prevPositionRef = useRef(
      new THREE.Vector3(data.position.x, data.position.y, data.position.z),
    );
    // Last polled XY used to derive a live-mode heading. Separate from
    // prevPositionRef (which is per-frame during playback).
    const lastPollRef = useRef(
      new THREE.Vector2(data.position.x, data.position.y),
    );
    // Live-mode yaw target; holds last accepted heading so a stationary
    // asset doesn't spin. null = not yet seeded.
    const targetYawRef = useRef<number | null>(null);

    const isEquipment = data.type === 'equipment';

    // Update target position when data changes, and derive a new live-mode
    // heading when the poll delta exceeds the jitter threshold.
    useEffect(() => {
      targetPosition.current.set(data.position.x, data.position.y, data.position.z);

      const dx = data.position.x - lastPollRef.current.x;
      const dy = data.position.y - lastPollRef.current.y;
      if (Math.hypot(dx, dy) >= LIVE_YAW_MOVE_THRESHOLD) {
        const yawOffset = isEquipment ? EQUIPMENT_YAW_OFFSET : PERSONNEL_YAW_OFFSET;
        targetYawRef.current = Math.atan2(dy, dx) + yawOffset;
        lastPollRef.current.set(data.position.x, data.position.y);
      }
    }, [data.position.x, data.position.y, data.position.z, isEquipment]);

    // Lerp to target position for smooth movement + highlight pulse
    useFrame(() => {
      if (!groupRef.current) return;

      if (isPlayback) {
        // Position: read data.position prop directly (bypasses the
        // targetPosition.current useEffect lag — data.position is already a
        // per-frame-interpolated value from usePlaybackPositions).
        const px = data.position.x;
        const py = data.position.y;
        const pz = data.position.z;
        groupRef.current.position.set(px, py, pz);

        // Yaw: rotate the model to face direction of travel.
        if (modelGroupRef.current && model3D) {
          const dx = px - prevPositionRef.current.x;
          const dy = py - prevPositionRef.current.y;
          if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) {
            const yawOffset = isEquipment ? EQUIPMENT_YAW_OFFSET : PERSONNEL_YAW_OFFSET;
            const rawYaw = Math.atan2(dy, dx) + yawOffset;
            // 0.15 lerp → ~220 ms to close 70% of a turn (was 0.05 = ~670 ms,
            // which felt sluggish on sharp corners).
            currentYawRef.current = lerpAngle(currentYawRef.current, rawYaw, 0.15);
            modelGroupRef.current.rotation.z = currentYawRef.current;
          }
          prevPositionRef.current.set(px, py, pz);
        }
      } else {
        // Live mode: lerp to absorb polling jitter.
        currentPosition.current.lerp(targetPosition.current, 0.1);
        groupRef.current.position.copy(currentPosition.current);

        // Face direction of travel. Slower lerp than playback (0.08 vs 0.15)
        // because poll cadence is ~1 Hz — faster would whip the model on
        // every update. targetYawRef is only set when poll delta exceeds
        // LIVE_YAW_MOVE_THRESHOLD, so a stationary asset holds its heading.
        if (modelGroupRef.current && model3D && targetYawRef.current != null) {
          currentYawRef.current = lerpAngle(
            currentYawRef.current,
            targetYawRef.current,
            0.08,
          );
          modelGroupRef.current.rotation.z = currentYawRef.current;
        }
      }

      // Highlight pulse only in live mode — during playback the asset is
      // already the visual focus (chase camera, scene isolation) and the
      // pulsing scale makes the truck look like it's breathing while
      // driving.
      if (highlighted && !isPlayback) {
        const pulse = 1.2 + Math.sin(Date.now() * 0.005) * 0.2;
        groupRef.current.scale.setScalar(pulse);
      } else {
        groupRef.current.scale.setScalar(dimmed ? 0.6 : 1);
      }
    });

    const markerSize = data.type === 'equipment' ? 18 : 16;

    // Label offset: just above the 3D model or sprite (Z is up in this mine).
    // Equipment tight (18) because the 3D models are chunky; personnel
    // taller (24) so the label clears the miner model's head.
    const labelZOffset = model3D
      ? (isEquipment ? 20 : 26)
      : 5 + markerSize / 2 + 5;

    const handlePointerEnter = () => {
      document.body.style.cursor = 'pointer';
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = setTimeout(() => setHovered(true), HOVER_DELAY_MS);
    };
    const handlePointerLeave = () => {
      document.body.style.cursor = 'default';
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      setHovered(false);
    };
    useEffect(() => () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    }, []);

    const handleDoubleClick = (e: any) => {
      e.stopPropagation();
      onDoubleClick?.(data.position);
    };

    // Hover primary matches the in-scene label: equipment shows the
    // asset_id, personnel shows the human name. The secondary line carries
    // the "other" identifier (when distinct) so operators see both.
    const hoverPrimary = isEquipment ? (data.externalId ?? data.name) : data.name;
    const otherId = isEquipment ? data.name : data.externalId;
    const hoverNamePrefix = otherId && otherId !== hoverPrimary ? `${otherId} · ` : '';
    const hoverSecondary = `${hoverNamePrefix}${isEquipment ? 'Equipment' : 'Personnel'} · ${formatStatus(data.status)}`;
    // Place hover label near the top of the model/sprite.
    const hoverZ = model3D ? (isEquipment ? 22 : 28) : 5 + markerSize + 2;

    return (
      <group ref={groupRef} position={[data.position.x, data.position.y, data.position.z]}>
        {/* 3D model or fallback sprite */}
        {model3D ? (
          <group ref={modelGroupRef}>
            <Model3DInstance
              model={model3D}
              scale={modelScale}
              tintColor={isEquipment ? EQUIPMENT_MODEL_COLOR : PERSONNEL_MODEL_COLOR}
              onClick={() => onClick?.(data.id)}
              onDoubleClick={handleDoubleClick}
              onPointerEnter={handlePointerEnter}
              onPointerLeave={handlePointerLeave}
            />
          </group>
        ) : (
          <SpriteMarker
            data={data}
            markerSize={markerSize}
            onClick={() => onClick?.(data.id)}
            onDoubleClick={handleDoubleClick}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
          />
        )}

        {/* Ground glow beneath model */}
        {model3D && (
          <GroundGlow status={data.status} size={isEquipment ? 40 : 22} />
        )}

        {/* Label */}
        {showLabel && (
          <LabelSprite
            text={data.label || data.name}
            zOffset={labelZOffset}
            status={data.status}
            assetType={data.type}
            subType={data.subType}
          />
        )}

        <MarkerHoverLabel
          visible={hovered}
          position={[0, 0, hoverZ]}
          primary={hoverPrimary}
          secondary={hoverSecondary}
        />
      </group>
    );
  },
);

AssetMarker.displayName = 'AssetMarker';

// 3D model instance with color tinting
const Model3DInstance: React.FC<{
  model: THREE.Object3D;
  scale: number;
  tintColor?: string;
  onClick: () => void;
  onDoubleClick?: (e: any) => void;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
}> = React.memo(({model, scale, tintColor, onClick, onDoubleClick, onPointerEnter, onPointerLeave}) => {
  const clonedModel = useMemo(() => {
    const clone = model.clone(true);

    // Axis convention: GLBs are Y-up (glTF standard), scene is Z-up.
    // Use `rotation.set` (not `rotation.x =`) so any non-zero Y/Z yaw
    // baked into the GLB's root gets zeroed out at the same time — the
    // partial-assignment version was preserving a root yaw from the GLB
    // and making markers appear at a diagonal heading.
    clone.rotation.set(Math.PI / 2, 0, 0);

    clone.traverse((child: THREE.Object3D) => {
      if (child instanceof THREE.Mesh) {
        // Clone materials so each marker instance has its own
        if (Array.isArray(child.material)) {
          child.material = child.material.map((m: THREE.Material) => m.clone());
        } else if (child.material) {
          child.material = child.material.clone();
        }
      }
    });
    return clone;
  }, [model, tintColor]);

  // Pointer cursor on hover (also delegates to parent hover handler if any)
  const handlePointerEnter = () => {
    document.body.style.cursor = 'pointer';
    onPointerEnter?.();
  };
  const handlePointerLeave = () => {
    document.body.style.cursor = 'default';
    onPointerLeave?.();
  };

  useEffect(() => {
    return () => { document.body.style.cursor = 'default'; };
  }, []);

  return (
    <primitive
      object={clonedModel}
      scale={[scale, scale, scale]}
      onClick={(e: any) => {
        e.stopPropagation();
        onClick();
      }}
      onDoubleClick={onDoubleClick}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    />
  );
});

Model3DInstance.displayName = 'Model3DInstance';

// Fallback sprite marker (used when 3D model not available)
const SpriteMarker: React.FC<{
  data: RTLSMarkerData;
  markerSize: number;
  onClick: () => void;
  onDoubleClick?: (e: any) => void;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
}> = React.memo(({data, markerSize, onClick, onDoubleClick, onPointerEnter, onPointerLeave}) => {
  const texture = useMemo(
    () => getCachedMarkerTexture(data.type, data.status, data.subType, data.name),
    [data.type, data.status, data.subType, data.name],
  );

  const spriteMaterial = useMemo(
    () => {
      const mat = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
        sizeAttenuation: true,
      });
      mat.toneMapped = false; // prevent ACES tone mapping from washing out colors
      return mat;
    },
    [texture],
  );

  // Pointer cursor on hover (delegates to parent hover handler if any)
  const handlePointerEnter = () => {
    document.body.style.cursor = 'pointer';
    onPointerEnter?.();
  };
  const handlePointerLeave = () => {
    document.body.style.cursor = 'default';
    onPointerLeave?.();
  };

  useEffect(() => {
    return () => { document.body.style.cursor = 'default'; };
  }, []);

  return (
    <sprite
      material={spriteMaterial}
      scale={[markerSize, markerSize, 1]}
      position={[0, 0, 5]}
      onClick={(e: any) => {
        e.stopPropagation();
        onClick();
      }}
      onDoubleClick={onDoubleClick}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    />
  );
});

SpriteMarker.displayName = 'SpriteMarker';

// Ground glow beneath 3D models — radial gradient projected on ground plane
const GroundGlow: React.FC<{status: string; size: number}> = React.memo(
  ({status, size}) => {
    const color = STATUS_COLORS[status as keyof typeof STATUS_COLORS] || '#6B7280';

    const texture = useMemo(() => {
      const canvas = document.createElement('canvas');
      const sz = 128;
      canvas.width = sz;
      canvas.height = sz;
      const ctx = canvas.getContext('2d')!;

      // Radial gradient glow
      const gradient = ctx.createRadialGradient(sz / 2, sz / 2, 0, sz / 2, sz / 2, sz / 2);
      gradient.addColorStop(0, color);
      gradient.addColorStop(0.2, color + 'A0');
      gradient.addColorStop(0.5, color + '40');
      gradient.addColorStop(0.8, color + '10');
      gradient.addColorStop(1, 'transparent');

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(sz / 2, sz / 2, sz / 2, 0, Math.PI * 2);
      ctx.fill();

      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.needsUpdate = true;
      return tex;
    }, [color]);

    const material = useMemo(
      () =>
        new THREE.MeshBasicMaterial({
          map: texture,
          transparent: true,
          opacity: 0.7,
          depthWrite: false,
          side: THREE.DoubleSide,
        }),
      [texture],
    );

    const geometry = useMemo(() => new THREE.PlaneGeometry(size * 2, size * 2), [size]);

    return (
      <mesh
        geometry={geometry}
        material={material}
        position={[0, 0, 0.3]}
      />
    );
  },
);

GroundGlow.displayName = 'GroundGlow';

// Clean pill-style label: colored dot + name (matches dashboard mockup)
const LabelSprite: React.FC<{
  text: string;
  zOffset: number;
  status?: string;
  assetType?: string;
  subType?: string;
}> = React.memo(({text, zOffset, status = 'active'}) => {
  const statusColor = STATUS_COLORS[status as keyof typeof STATUS_COLORS] || '#6B7280';

  const {texture, aspect} = useMemo(() => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    const s = 4; // retina scale
    const fontSize = 24 * s;
    const paddingH = 20 * s;
    const paddingV = 12 * s;
    const dotR = 8 * s;
    const dotGap = 12 * s;

    // Measure name width
    ctx.font = `600 ${fontSize}px Inter, -apple-system, BlinkMacSystemFont, sans-serif`;
    const nameW = ctx.measureText(text).width;

    // Pill dimensions
    const totalW = paddingH + dotR * 2 + dotGap + nameW + paddingH;
    const totalH = fontSize + paddingV * 2;
    const borderRadius = totalH / 2;

    canvas.width = totalW;
    canvas.height = totalH;

    // Drop shadow
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 8 * s;
    ctx.shadowOffsetY = 2 * s;
    ctx.beginPath();
    ctx.roundRect(0, 0, totalW, totalH, borderRadius);
    ctx.fillStyle = 'rgba(30, 36, 51, 0.85)';
    ctx.fill();
    ctx.restore();

    // Background fill (no shadow)
    ctx.beginPath();
    ctx.roundRect(0, 0, totalW, totalH, borderRadius);
    ctx.fillStyle = 'rgba(30, 36, 51, 0.85)';
    ctx.fill();

    // Border
    ctx.beginPath();
    ctx.roundRect(0, 0, totalW, totalH, borderRadius);
    ctx.strokeStyle = 'rgba(75, 85, 99, 0.5)';
    ctx.lineWidth = 1.5 * s;
    ctx.stroke();

    const cy = totalH / 2;
    let x = paddingH;

    // Status dot glow
    const dotCx = x + dotR;
    ctx.save();
    ctx.shadowColor = statusColor;
    ctx.shadowBlur = 10 * s;
    ctx.fillStyle = statusColor;
    ctx.beginPath();
    ctx.arc(dotCx, cy, dotR, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Status dot solid
    ctx.fillStyle = statusColor;
    ctx.beginPath();
    ctx.arc(dotCx, cy, dotR, 0, Math.PI * 2);
    ctx.fill();
    x += dotR * 2 + dotGap;

    // Name text
    ctx.fillStyle = '#FFFFFF';
    ctx.font = `600 ${fontSize}px Inter, -apple-system, BlinkMacSystemFont, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x, cy);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.needsUpdate = true;
    return {texture: tex, aspect: totalW / totalH};
  }, [text, status, statusColor]);

  // Scale sprite proportional to text width, fixed height
  const spriteHeight = 5;
  const spriteWidth = spriteHeight * aspect;

  const material = useMemo(
    () => {
      const mat = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
        sizeAttenuation: true,
      });
      mat.toneMapped = false;
      return mat;
    },
    [texture],
  );

  return (
    <sprite
      material={material}
      position={[0, 0, zOffset]}
      scale={[spriteWidth, spriteHeight, 1]}
    />
  );
});

LabelSprite.displayName = 'LabelSprite';
